import { DOCUMENT, Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AGUAppSurfaceHostAdapter } from './app-surface-host.adapter';
import { BuildLocationSearch, IsSameLocation, LOCATION_PARAM_KEYS, WithOneLevelFrom, type AGUAppLocation } from './app-location';

/**
 * A host adapter that mirrors the in-surface location into the tab's visible
 * URL (`?m=&v=&id=`, `LOCATION_PARAM_KEYS` in
 * `app-location.ts`). The base adapter's `NavigateTo`/`ResetTo`/`Back` only
 * update the in-memory `Location$` stream — MAPS (the other consumer of the
 * base adapter) never bridges that to the URL at all, and this surface used to
 * do it through `BaseResourceComponent.UpdateQueryParams()`
 * (`NavigationService`'s tab-configuration plumbing), which is the framework's
 * own documented mechanism ("Use this instead of directly calling
 * router.navigate()..."). That mechanism is broken for THIS surface's tab
 * shape: `@memberjunction/ng-explorer-core@5.51.0`'s
 * `ShellComponent.buildResourceUrl()` never calls `appendQP()` on the
 * `isAppDefault && appName` branch (dist/lib/shell/shell.component.js:1677-1680)
 * — the shape of a composite app's default-dashboard tab (Research
 * Accelerator, Grant Finder, People Finder and Job Finder all have zero
 * `DefaultNavItems`, so `BaseApplication.loadDefaultDashboard()` is what
 * creates their tab, always with `isAppDefault: true` and no `navItemName`).
 * The tab's `Configuration.queryParams` gets written correctly; the visible
 * URL `syncUrlWithWorkspace()` recomputes from it never includes them, and —
 * once anything writes params onto the address bar by other means — that
 * recomputation actively strips them back off via a real
 * `router.navigateByUrl()`.
 *
 * Writing directly through Angular's `Location` service (`go()` for
 * `NavigateTo`/`Back` — genuine, walkable browser-history entries; `replaceState()`
 * for `ResetTo` — landing/entitlement-correction/popstate-resync, "not a place
 * the user navigated to" per the base adapter's own doc comment) sidesteps the
 * bug rather than fighting it: `Location.go()`/`replaceState()` do not feed the
 * POP-only stream `Router` listens on for triggering navigations (verified in
 * `@angular/common`'s `Location` — only `_locationStrategy.onPopState()` feeds
 * `_subject`, which is what `Router`'s `setUpLocationChangeListener` filters on
 * `event['type'] === 'popstate'`), so `Router.url` never reflects the write and
 * `syncUrlWithWorkspace`'s `currentUrl !== newUrl` check never fires an undo. A
 * genuine browser Back/Forward still round-trips correctly: it's a real
 * `popstate`, which `Router` DOES react to, and `syncWorkspaceWithUrl()`'s
 * `findTabForUrl()` matches this tab by path alone (`appOnlyMatch`, query
 * params stripped before matching) — so it finds this tab regardless of which
 * `?m=/&v=/&id=` combination is in the URL, and delivers the change through
 * `OnQueryParamsChanged` exactly as `RaSurfaceComponent` already expects.
 *
 * THE ONE PLACE THE SHELL STILL STRIPS THE URL: a genuine browser Back/Forward.
 * That popstate is a real Router navigation, so afterwards `Router.url` is the
 * full `/app/<app>?m=&v=&id=` URL. `syncWorkspaceWithUrl()` then writes the
 * popped params into the tab configuration (shell.component.js:1214-1240); its
 * `urlBasedNavigation` guard is reset in a synchronous `finally`, but the
 * workspace Configuration subscriber that reacts to that write is async —
 * `await this.syncActiveAppWithTab(config)` (shell.component.js:1008) runs
 * before `this.syncUrlWithWorkspace(config)` (:1009), so by the time
 * `syncUrlWithWorkspace` checks the guard (:1180) it is already false. It
 * recomputes the bare URL (:1677-1680), sees `bare !== Router.url`, and calls
 * `router.navigateByUrl('/app/<app>')` (:1202) — an imperative navigation the
 * Router commits with `pushState` (`HistoryStateManager.setBrowserUrl`,
 * `@angular/router` 21.1.3 `_router-chunk.mjs:4234-4254`). That push discards
 * every Forward entry and leaves the address bar bare (a reload lands Home).
 * Re-arming a flag inside the shell's ordering is not available from here, so
 * `KeepUrlOnShellResync` neutralizes the push itself: when an imperative
 * navigation targets exactly the bare path of the page we are on, that path is
 * one of this surface's own apps, and the address bar carries a surface
 * location, the navigation is switched to
 * `skipLocationChange`. The Router still navigates (its state becomes the bare
 * URL — the steady state the `Location.go()` writes above rely on), but the
 * browser entry, its query and the Forward stack are left alone.
 * `NavigationStart` is emitted before the deferred URL update
 * (`BeforeActivateRoutes`, `_router-chunk.mjs:4232-4236`), and the `extras`
 * that update reads is the same object `Router.currentNavigation()` exposes
 * (`_router-chunk.mjs:3714`, handed to the state manager at :4415), so setting
 * the flag there is honored. For an app-default tab the shell can only ever
 * compute the bare URL, so such a navigation never carries a place the user
 * asked for; the surface's location lives only in the query.
 *
 * Promoted from MJExplorer's `ExplorerRaHostAdapter` so every Explorer-hosted
 * surface whose tab is an app-default dashboard (Research Accelerator, OEP)
 * shares one implementation of the workaround.
 */
@Injectable()
export class AGUUrlSyncedHostAdapter extends AGUAppSurfaceHostAdapter {
    private readonly ngLocation = inject(Location);
    private readonly doc = inject<Document>(DOCUMENT);
    /**
     * The Router, with the shell-resync watch attached as it is injected. Done from the field
     * initializer because that runs in the injection context (`takeUntilDestroyed` ends the
     * watch with the injector that owns this adapter) while the base adapter's DI constructor
     * stays inherited as-is.
     */
    private readonly ngRouter: Router = this.WatchForShellResync(inject(Router));

    override NavigateTo(location: AGUAppLocation): void {
        // Same place, same URL: a push here would be a Back step that goes nowhere.
        // Compared one level deep, as the base adapter stores it, so a nested origin cannot fake a move.
        if (IsSameLocation(WithOneLevelFrom(location), this.CurrentLocation)) return;
        super.NavigateTo(location);
        this.WriteLocationToUrl(this.CurrentLocation, 'push');
    }

    override ResetTo(location: AGUAppLocation): void {
        super.ResetTo(location);
        this.WriteLocationToUrl(location, 'replace');
    }

    /**
     * `Back()` moves to a genuinely different, already-visited place (per the
     * base adapter, popped off its own back stack) — from the URL's
     * perspective that's the same kind of event as `NavigateTo`, so it pushes
     * too. That keeps the browser's real Back/Forward walkable in the same
     * direction: pressing browser Back after this undoes it by returning to
     * where `Back()` moved away from.
     */
    override Back(): void {
        const before = this.CurrentLocation;
        super.Back();
        const after = this.CurrentLocation;
        if (after !== before) {
            this.WriteLocationToUrl(after, 'push');
        }
    }

    private WatchForShellResync(router: Router): Router {
        router.events
            .pipe(
                filter((e): e is NavigationStart => e instanceof NavigationStart),
                takeUntilDestroyed()
            )
            .subscribe((e) => this.KeepUrlOnShellResync(e));
        router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                takeUntilDestroyed()
            )
            .subscribe(() => this.RestoreLocationInUrl());
        return router;
    }

    /**
     * Re-entry through the app tile or switcher (X-3, 2026-10-08): MJ re-attaches the cached
     * surface, which still shows the module the member left, but the shell navigates to the bare
     * app URL (`/app/research-accelerator`), so a reload or bookmark from there landed on the
     * default module. When a navigation ends on one of this surface's own app paths with an empty
     * query, put the location the surface is showing back — `replaceState`, as
     * for any `ResetTo`, since the member did not navigate anywhere new inside the surface.
     */
    private RestoreLocationInUrl(): void {
        const [path, query = ''] = this.ngLocation.path().split('?');
        // Only the bare app URL the shell writes: any query (a location, or anything else) is left as it is.
        if (query.length > 0 || !this.OwnAppPaths().has(path.toLowerCase())) return;
        this.WriteLocationToUrl(this.CurrentLocation, 'replace');
    }

    /**
     * See the class comment: keep the shell's bare-URL resync off the browser history.
     *
     * Scoped to this surface's OWN app URLs (`OwnApplicationNames` → `ApplicationManager.GetAppUrl`,
     * the same `/app/<Path || Name>` the shell's `buildResourceUrl` builds): every live surface's
     * adapter sees every Router navigation, including a hidden tab's, and must never act on another
     * app's URLs. Within its own app path the adapter's surface IS that tab's content, so no
     * separate "is mounted" check is needed.
     *
     * The shell's app-switcher click on the app already open (`onAppSwitch`, shell.component.js:2180,
     * last branch: `navigateByUrl(buildResourceUrl(firstTab))`) has the same shape and is suppressed
     * too: before, it pushed the bare URL while the surface kept showing the same view (the surface
     * is only told about popstate); now the address bar keeps describing that view and no entry is
     * added. The surface's own nav (Home etc.) goes through `NavigateTo`, never through here.
     */
    private KeepUrlOnShellResync(e: NavigationStart): void {
        if (e.navigationTrigger !== 'imperative') return;
        const [path, query = ''] = this.ngLocation.path().split('?');
        if (e.url.toLowerCase() !== path.toLowerCase() || query.length === 0) return;
        if (!this.OwnAppPaths().has(path.toLowerCase())) return;
        if (!new URLSearchParams(query).get(LOCATION_PARAM_KEYS.Module)) return;
        const navigation = this.ngRouter.currentNavigation();
        if (navigation && navigation.id === e.id) {
            navigation.extras.skipLocationChange = true;
        }
    }

    /**
     * Resolved per navigation: ApplicationManager loads the user's apps after this adapter exists. Lower-cased, and every
     * caller lower-cases the path it checks: MJ resolves `/app/<segment>` case-insensitively (ApplicationManager
     * GetAppByPath), so the OEP emails' `/app/OEP` is the same app as `GetAppUrl`'s `/app/oep` (Path 'oep').
     */
    private OwnAppPaths(): Set<string> {
        const paths = new Set<string>();
        for (const name of this.config.OwnApplicationNames) {
            const app = this.apps.GetAppByName(name);
            if (app) paths.add(this.apps.GetAppUrl(app).toLowerCase());
        }
        return paths;
    }

    /**
     * Writes only onto this surface's own app URL. The shell can boot a surface whose tab is not the
     * page on screen — a login that returns to `/app/maps` while the saved workspace still holds a
     * Research Accelerator tab — and its landing `ResetTo` then wrote `?m=dashboard&v=home` onto
     * `/app/maps` (2026-10-26 run, J-12c). The in-memory location still moves; `RestoreLocationInUrl`
     * puts it in the address bar when the shell brings this surface's own URL up.
     */
    private WriteLocationToUrl(location: AGUAppLocation, mode: 'push' | 'replace'): void {
        if (!location.ModuleKey) return;
        const path = this.doc.location.pathname;
        if (!this.OwnAppPaths().has(path.toLowerCase())) return;
        const query = BuildLocationSearch(location);
        if (mode === 'push') {
            this.ngLocation.go(path, query);
        } else {
            this.ngLocation.replaceState(path, query);
        }
    }
}
